import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

import { cn } from '../cn';
import { CheckIcon, DangerIcon, InfoIcon, WarningIcon } from '../icons';

/**
 * Non-blocking feedback. `alert()` is banned outright and this is what replaces
 * it (`frontend.md` §6).
 *
 * One live region per surface, not one per toast: `polite` for a notice,
 * `assertive` for a failure. A toast never carries a decision — a decision goes
 * through `<ConfirmDialog>`, which is a view of its own.
 *
 * **THE STACK SITS AT THE TOP OF THE VIEWPORT, CENTRED, AND THAT IS A BUG FIX
 * RATHER THAN A PREFERENCE.** It used to sit at `inset-block-end: 0` /
 * `inset-inline-start: 0` — the bottom corner. On a console whose forms run to
 * several screens, an operator who presses a save button that is itself halfway
 * down the page looks at the button, and the answer appeared in a corner outside
 * the region they were reading. Centred at the top, one position serves every
 * page in the console whatever its length, and it is the same position the
 * subscriber and public hosts now use — three surfaces, one place to look.
 *
 * **The chrome is `<Alert>`'s, deliberately.** A notice that reads one way
 * inline and another way floating is two vocabularies for one fact: the same
 * four kinds, the same soft fill, the same icon, so «saved» looks like «saved»
 * wherever it is drawn. The icon is what distinguishes the kinds — a coloured
 * edge alone would identify them by hue only (SC 1.4.1).
 *
 * **The whole toast stays one button.** Pressing anywhere on it dismisses it and
 * does nothing else, which is what it has always done here; a dedicated close
 * control inside it would put two tab stops on one notice that carries no
 * decision.
 */

export type ToastIntent = 'success' | 'warning' | 'danger' | 'info';

interface Toast {
  id: number;
  intent: ToastIntent;
  message: string;
}

interface ToastApi {
  show: (message: string, intent?: ToastIntent) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_TTL_MS = 6_000;

const INTENT_CLASSES: Record<ToastIntent, string> = {
  success: 'border-success bg-success-soft',
  warning: 'border-warning bg-warning-soft',
  danger: 'border-danger bg-danger-soft',
  info: 'border-info bg-info-soft',
};

const INTENT_ICONS: Record<ToastIntent, typeof InfoIcon> = {
  success: CheckIcon,
  warning: WarningIcon,
  danger: DangerIcon,
  info: InfoIcon,
};

const INTENT_ICON_CLASSES: Record<ToastIntent, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, intent: ToastIntent = 'info') => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, intent, message }]);
      window.setTimeout(() => dismiss(id), DEFAULT_TTL_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  const assertive = toasts.filter((toast) => toast.intent === 'danger');
  const polite = toasts.filter((toast) => toast.intent !== 'danger');

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Rendered as a sibling of the app, never inside a `transform` /
          `backdrop-filter` ancestor — either re-scopes `position: fixed`.

          `inset-inline: 0` plus `items-center` is what centres the stack without
          a `translate`, which would re-scope `fixed` for anything inside it. The
          band itself takes no pointer events so it never covers the top bar's
          controls; each toast takes them back. */}
      <div className="pointer-events-none fixed end-0 start-0 top-0 z-50 flex flex-col items-center gap-2 p-4">
        <ToastRegion toasts={polite} politeness="polite" onDismiss={dismiss} />
        <ToastRegion toasts={assertive} politeness="assertive" onDismiss={dismiss} />
      </div>
    </ToastContext.Provider>
  );
}

function ToastRegion({
  toasts,
  politeness,
  onDismiss,
}: {
  toasts: Toast[];
  politeness: 'polite' | 'assertive';
  onDismiss: (id: number) => void;
}) {
  return (
    <div aria-live={politeness} className="flex flex-col items-center gap-2">
      {toasts.map((toast) => {
        const Glyph = INTENT_ICONS[toast.intent];

        return (
          <button
            key={toast.id}
            type="button"
            onClick={() => onDismiss(toast.id)}
            className={cn(
              'pointer-events-auto flex max-w-measure items-start gap-3 rounded-md border border-s-4 p-4 text-start text-sm text-ink shadow-3',
              INTENT_CLASSES[toast.intent],
            )}
          >
            <Glyph className={cn('mt-1 shrink-0', INTENT_ICON_CLASSES[toast.intent])} />
            <span className="min-w-0">{toast.message}</span>
          </button>
        );
      })}
    </div>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be called inside <ToastProvider>.');
  return api;
}
